import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import useTranslation from '../../hooks/useTranslation';
import { useAuth } from '../../auth/AuthProvider';
import UserAvatar from '../../components/ui/UserAvatar';

import ProjectDetailFilterBar from './components/ProjectDetailFilterBar';
import NewProjectModal from './components/NewProjectModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import { projectService } from '../../services/projectService';
import projectCostService from '../../services/projectCostService';
import PortalDropdown from '../../components/ui/PortalDropdown';
import { canEditProject } from '../../utils/permissions';
import { seedDefaultFolders } from '../../services/projectDocumentsService';
import { formatCurrency as fmtCAD } from '../../utils/formatCurrency';
import { statusVariant } from '../../utils/statusVariant';
import Badge from '../../components/ui/Badge';
import ProjectStatusMenu from './components/ProjectStatusMenu';

// Helper: get initials from full_name or email
const getInitials = (name, email) => {
  if (name) {
    const parts = name?.trim()?.split(' ');
    if (parts?.length >= 2) return (parts?.[0]?.[0] + parts?.[parts?.length - 1]?.[0])?.toUpperCase();
    return parts?.[0]?.[0]?.toUpperCase() || '?';
  }
  return email?.[0]?.toUpperCase() || '?';
};

// Small avatar circle with tooltip — uses UserAvatar for photo support
const TeamAvatar = ({ member, role, index = 0 }) => {
  if (!member) return null;
  const displayName = member?.full_name || member?.email || '';
  // Overlapping stack: each next avatar tucks under the previous (earlier = on top).
  const Z = ['z-40', 'z-30', 'z-20', 'z-10'];
  return (
    <div
      className={`relative group/avatar -ml-2 first:ml-0 ${Z[index] || 'z-10'}`}
      title={`${role}: ${displayName}`}
    >
      <UserAvatar user={member} size={24} className="ring-2 ring-white" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/avatar:block z-50 pointer-events-none">
        <div className="bg-gray-900 text-white text-[10px] rounded px-2 py-1 whitespace-nowrap shadow-lg">
          <span className="text-gray-400">{role}: </span>{displayName}
        </div>
      </div>
    </div>
  );
};

const SORTABLE_COLUMNS = ['projectNumber', 'name', 'revisedContractValue', 'margin', 'status'];
const LS_SORT_KEY = 'onsitepm_projectList_sort';

// Exclusion list: show ALL projects EXCEPT these statuses (both exist in the ENUM — safe)
// This avoids referencing any ENUM value that might not exist (e.g. 'pending', 'completed', 'cancelled')
const PROJECT_LIST_EXCLUDE_STATUSES = ['archived', 'deleted'];

// Statuses available in the inline dropdown (per spec: 4 values)
const STATUS_DROPDOWN_OPTIONS = ['active', 'on-hold', 'completed', 'cancelled'];

const getInitialSortConfig = () => {
  try {
    const stored = localStorage.getItem(LS_SORT_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (
        parsed &&
        typeof parsed?.key === 'string' &&
        SORTABLE_COLUMNS?.includes(parsed?.key) &&
        (parsed?.direction === 'asc' || parsed?.direction === 'desc')
      ) {
        return parsed;
      }
    }
  } catch (_) {}
  return { key: 'name', direction: 'asc' };
};

const ProjectDetailPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState(getInitialSortConfig());
  const [activeFilters, setActiveFilters] = useState({
    universalSearch: '',
    clientFilter: '',
    yearFilter: null
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  // Projets dont le statut est en cours d'ecriture : un seul changement a la fois par ligne.
  const [savingStatusIds, setSavingStatusIds] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [financialMap, setFinancialMap] = useState({});
  const [statusError, setStatusError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // Refs map for PortalDropdown anchors — keyed by project id
  const actionBtnRefs = useRef({});

  // Mirror projects into a ref so applyFilters always reads the latest array
  const projectsRef = useRef(projects);
  useEffect(() => { projectsRef.current = projects; }, [projects]);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await projectService?.getAll({ excludeStatuses: PROJECT_LIST_EXCLUDE_STATUSES });
      setProjects(data);
      setFilteredProjects(data);

      // Fetch computed financial values for all projects (same logic as BudgetPage)
      if (data?.length > 0) {
        const ids = data?.map(p => p?.id);
        const fMap = await projectCostService?.getFinancialSummaryForProjects(ids);
        setFinancialMap(fMap || {});
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      setLoadError(error?.message || 'Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Derive unique client list from loaded projects
  const uniqueClients = React.useMemo(() => {
    const set = new Set();
    projects?.forEach(p => { if (p?.client) set?.add(p?.client); });
    return Array.from(set)?.sort();
  }, [projects]);

  const applyFilters = (filters) => {
    let filtered = [...projectsRef?.current];

    if (filters?.universalSearch) {
      const query = filters?.universalSearch?.toLowerCase();
      filtered = filtered?.filter((p) =>
        p?.name?.toLowerCase()?.includes(query) ||
        p?.projectNumber?.toLowerCase()?.includes(query) ||
        p?.client?.toLowerCase()?.includes(query)
      );
    }

    if (filters?.clientFilter) {
      filtered = filtered?.filter((p) => p?.client === filters?.clientFilter);
    }

    if (filters?.yearFilter != null) {
      const year = filters?.yearFilter;
      const yearStart = new Date(`${year}-01-01`);
      const yearEnd = new Date(`${year}-12-31`);
      filtered = filtered?.filter((p) => {
        if (!p?.startDate) return true;
        const start = new Date(p.startDate);
        // Treat missing or invalid endDate (e.g. '0001-01-01') as unknown → include in all years
        const isInvalidEndDate = !p?.endDate || p?.endDate === '0001-01-01' || p?.endDate?.startsWith('0001-');
        if (isInvalidEndDate) return start <= yearEnd;
        const end = new Date(p.endDate);
        return start <= yearEnd && end >= yearStart;
      });
    }

    setFilteredProjects(filtered);
    setActiveFilters(filters);
  };

  const handleNewProject = () => {
    setEditingProject(null);
    setIsModalOpen(true);
  };

  const handleCreateProject = async (projectData) => {
    console.log("📥 handleCreateProject triggered");
    console.log('📥 handleCreateProject called', projectData);
    try {
      console.log("🗄️ Calling projectService.createProject");
      const newProject = await projectService?.createProject(projectData);
      console.log("✅ DB insert success");
      console.log('✅ Project successfully created, reloading list');

      // Best-effort: seed default folder structure for the new project.
      // Failures here do NOT roll back the project creation.
      if (newProject?.id) {
        seedDefaultFolders(newProject?.id)?.catch((err) => {
          console.warn('[handleCreateProject] seedDefaultFolders failed silently:', err);
        });
      }

      await loadProjects();
    } catch (error) {
      console.error("❌ handleCreateProject error:", error);
      console.error('Error creating project:', error);
      throw error;
    }
  };

  const handleUpdateProject = async (projectData) => {
    console.log("📝 handleUpdateProject triggered");
    try {
      const updatedProject = await projectService?.update(editingProject?.id, projectData);
      
      if (!updatedProject) {
        throw new Error('Update failed: No data returned');
      }
      
      console.log('[PROJECT UPDATED]', updatedProject);
      
      // Patch state properly with returned data
      setProjects(prev =>
        prev?.map(p =>
          p?.id === updatedProject?.id ? updatedProject : p
        )
      );
      
      // Reapply filters to update filtered list
      setFilteredProjects(prev => 
        prev?.map(p => 
          p?.id === updatedProject?.id ? updatedProject : p
        )
      );
      
      // Modal will be closed by the modal component after this resolves
    } catch (error) {
      console.error('[UPDATE ERROR]', error);
      console.error("❌ handleUpdateProject error:", error);
      throw error;
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    const newConfig = { key, direction };
    setSortConfig(newConfig);
    try {
      localStorage.setItem(LS_SORT_KEY, JSON.stringify(newConfig));
    } catch (_) {}
  };

  const sortedProjects = [...filteredProjects]?.sort((a, b) => {
    let aValue, bValue;

    switch (sortConfig?.key) {
      case 'projectNumber':
        aValue = a?.projectNumber?.toLowerCase() || '';
        bValue = b?.projectNumber?.toLowerCase() || '';
        break;
      case 'name':
        aValue = a?.name?.toLowerCase() || '';
        bValue = b?.name?.toLowerCase() || '';
        break;
      case 'revisedContractValue':
        aValue = financialMap?.[a?.id]?.revisedContractValue ?? 0;
        bValue = financialMap?.[b?.id]?.revisedContractValue ?? 0;
        break;
      case 'margin':
        aValue = financialMap?.[a?.id]?.profitMargin ?? 0;
        bValue = financialMap?.[b?.id]?.profitMargin ?? 0;
        break;
      case 'status':
        aValue = a?.status || '';
        bValue = b?.status || '';
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleRowClick = (projectId) => {
    // Find the project data to pass in navigation state
    const projectData = sortedProjects?.find(p => p?.id === projectId);
    navigate(`/project/${projectId}`, { state: { project: projectData } });
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'on-hold':
        return 'bg-gray-100 text-gray-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Returns true if the current user can edit this project's status (uses centralized helper)
  const canEditStatus = (project) => {
    if (!userProfile) return false;
    return canEditProject(project, userProfile);
  };

  const handleStatusChange = async (project, newStatus) => {
    if (project?.status === newStatus) return;
    // Un changement est deja en cours pour ce projet : on attend sa reponse.
    if (savingStatusIds?.includes(project?.id)) return;
    setSavingStatusIds(prev => [...prev, project?.id]);

    const previousStatus = project?.status;

    // Optimistic update
    const patchStatus = (list) =>
      list?.map(p => p?.id === project?.id ? { ...p, status: newStatus } : p);
    setProjects(prev => patchStatus(prev));
    setFilteredProjects(prev => patchStatus(prev));

    try {
      // IMPORTANT: Only send status — do NOT send team fields here to avoid
      // overwriting pm_user_id / supervisor_user_id / assistant_user_id with
      // stale or null values. coordinator_user_id is never sent.
      await projectService?.updateStatus(project?.id, newStatus);
    } catch (err) {
      console.error('Error updating project status:', err);
      // Revert on failure
      const revertStatus = (list) =>
        list?.map(p => p?.id === project?.id ? { ...p, status: previousStatus } : p);
      setProjects(prev => revertStatus(prev));
      setFilteredProjects(prev => revertStatus(prev));
      setStatusError(t('common.status') + ': ' + (err?.message || 'Update failed'));
      setTimeout(() => setStatusError(null), 4000);
    } finally {
      setSavingStatusIds(prev => prev?.filter(id => id !== project?.id));
    }
  };

  const toggleDropdown = (e, projectId) => {
    e?.stopPropagation();
    setOpenDropdownId(openDropdownId === projectId ? null : projectId);
  };

  const handleEdit = (e, projectId) => {
    e?.stopPropagation();
    setOpenDropdownId(null);
    
    const project = projects?.find(p => p?.id === projectId);
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const handleArchive = async (e, projectId) => {
    e?.stopPropagation();
    setOpenDropdownId(null);
    
    try {
      await projectService?.archive(projectId);
      // Immediately remove from display
      setProjects(prev => prev?.filter(p => p?.id !== projectId));
      setFilteredProjects(prev => prev?.filter(p => p?.id !== projectId));
    } catch (error) {
      console.error('Error archiving project:', error);
    }
  };

  const handleDelete = (e, projectId) => {
    e?.stopPropagation();
    setOpenDropdownId(null);
    
    const project = projects?.find(p => p?.id === projectId);
    setProjectToDelete(project);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;
    
    try {
      await projectService?.softDelete(projectToDelete?.id);
      // Immediately remove from display
      setProjects(prev => prev?.filter(p => p?.id !== projectToDelete?.id));
      setFilteredProjects(prev => prev?.filter(p => p?.id !== projectToDelete?.id));
      setProjectToDelete(null);
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' ? (
      <Icon name="ChevronUp" size={16} className="text-primary" />
    ) : (
      <Icon name="ChevronDown" size={16} className="text-primary" />
    );
  };

  return (
    <div className="pt-0 pb-0 px-0">
      {/* Status error toast */}
      {statusError && (
        <div className="fixed top-4 right-4 z-[10000] bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
          <span>{statusError}</span>
        </div>
      )}
      {/* Header Block */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="FolderKanban" size={20} className="text-primary" />
          <h1 className="text-lg font-semibold text-[#2C3E50]">
            {t("projectList.title")}
          </h1>
        </div>
        <p className="text-xs font-normal text-[#6B7280]">
          {t("projectList.subtitle")}
        </p>
      </div>
      {/* Content Wrapper */}
      <div className="space-y-6">
        {/* Filter Section — le bouton partage la ligne des filtres */}
        <div className="mb-4 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <ProjectDetailFilterBar
              onFilterChange={applyFilters}
              activeFilters={activeFilters}
              projectCount={filteredProjects?.length}
              clients={uniqueClients}
            />
          </div>
          <button
            onClick={handleNewProject}
            className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-white bg-primary hover:bg-[#FF5A1A] rounded-lg transition-colors shadow-sm shrink-0"
          >
            <Icon name="Plus" size={13} />
            {t("projectList.newProject")}
          </button>
        </div>

        {/* Table Section */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={32} className="animate-spin text-primary" />
          </div>
        ) : loadError ? (
          <div className="p-12 text-center bg-white rounded-lg border border-red-200 shadow-sm">
            <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-red-400" />
            <p className="text-red-600 font-medium mb-1">Failed to load projects</p>
            <p className="text-sm text-gray-500 mb-4">{loadError}</p>
            <button
              onClick={loadProjects}
              className="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-dark transition-colors"
            >
              Retry
            </button>
          </div>
        ) : filteredProjects?.length === 0 && projects?.length === 0 ? (
          // No projects at all — user has 0 assigned projects
          (<div className="p-12 text-center bg-white rounded-lg border border-gray-200 shadow-sm">
            <Icon name="FolderOpen" size={48} className="mx-auto mb-4 text-[#6B7280]" />
            <p className="text-[#374151] font-medium mb-1">{t("projectList.noProjectsAssigned.title")}</p>
            <p className="text-sm text-[#6B7280]">{t("projectList.noProjectsAssigned.message")}</p>
          </div>)
        ) : filteredProjects?.length === 0 ? (
          // Filters returned no results
          (<div className="p-12 text-center bg-white rounded-lg border border-gray-200 shadow-sm">
            <Icon name="FolderOpen" size={48} className="mx-auto mb-4 text-[#6B7280]" />
            <p className="text-[#6B7280]">{t("projectList.filter.empty")}</p>
          </div>)
        ) : (
          <div className="space-y-2">
            {/* Table Header */}
            <div className="flex items-center h-12 relative">
              {/* Project # — sortable, ~100px */}
              <div
                className="px-3 text-xs font-semibold text-foreground/80 flex items-center gap-1 cursor-pointer hover:bg-muted/30 transition-colors flex-shrink-0"
                style={{ width: '100px' }}
                onClick={() => handleSort('projectNumber')}
              >
                <span>{t("projectList.filter.projectNumber")}</span>
                <SortIcon columnKey="projectNumber" />
              </div>
              {/* Project Name — flex-grow, sortable */}
              <div
                className="px-3 text-xs font-semibold text-foreground/80 flex items-center gap-1 cursor-pointer hover:bg-muted/30 transition-colors min-w-0 flex-1"
                onClick={() => handleSort('name')}
              >
                <span>{t("projectList.table.name")}</span>
                <SortIcon columnKey="name" />
              </div>
              {/* Contract Value (revised) — sortable, ~140px right-aligned */}
              <div
                className="px-3 text-xs font-semibold text-foreground/80 flex items-center justify-end gap-1 cursor-pointer hover:bg-muted/30 transition-colors flex-shrink-0"
                style={{ width: '140px' }}
                onClick={() => handleSort('revisedContractValue')}
              >
                <span className="text-right leading-tight">{t("projectList.table.contractValueRevised")}</span>
                <SortIcon columnKey="revisedContractValue" />
              </div>
              {/* Margin % — sortable, ~80px right-aligned */}
              <div
                className="px-3 text-xs font-semibold text-foreground/80 flex items-center justify-end gap-1 cursor-pointer hover:bg-muted/30 transition-colors flex-shrink-0"
                style={{ width: '80px' }}
                onClick={() => handleSort('margin')}
              >
                <span>{t("projectList.table.margin")} %</span>
                <SortIcon columnKey="margin" />
              </div>
              {/* Status — sortable, ~130px */}
              <div
                className="px-3 text-xs font-semibold text-foreground/80 flex items-center gap-1 cursor-pointer hover:bg-muted/30 transition-colors flex-shrink-0"
                style={{ width: '130px' }}
                onClick={() => handleSort('status')}
              >
                <span>{t("projectList.table.status")}</span>
                <SortIcon columnKey="status" />
              </div>
              {/* Project Team — NOT sortable, overlapping avatar stack */}
              <div className="px-3 text-xs font-semibold text-foreground/80 flex-shrink-0" style={{ width: '100px' }}>
                <span>{t("projectTeam.sectionTitle")}</span>
              </div>
              {/* Actions — sticky right, ~50px */}
              <div
                className="px-3 text-xs font-semibold text-foreground/80 text-center flex-shrink-0 sticky right-0 bg-white"
                style={{ width: '50px' }}
              >
                <span>{t("projectList.table.actions")}</span>
              </div>
            </div>

            {/* Row Cards */}
            {sortedProjects?.map((project) => {
              const fin = financialMap?.[project?.id] || {};
              const revisedContractValue = fin?.revisedContractValue ?? 0;
              const margin = fin?.profitMargin ?? 0;

              return (
                <div
                  key={project?.id}
                  onClick={() => handleRowClick(project?.id)}
                  className="flex items-center h-10 bg-white border border-gray-200 rounded-lg shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_0_rgba(0,0,0,0.02)] transition-all duration-200 cursor-pointer hover:border-primary relative"
                >
                  {/* Project # */}
                  <div className="px-3 text-xs leading-none text-primary flex-shrink-0 overflow-hidden" style={{ width: '100px' }}>
                    <span className="truncate block">{project?.projectNumber || 'N/A'}</span>
                  </div>
                  {/* Project Name — flex-grow, truncate with tooltip */}
                  <div
                    className="px-3 text-xs leading-none text-foreground min-w-0 flex-1 overflow-hidden"
                    title={project?.name}
                  >
                    <span className="truncate block">{project?.name}</span>
                  </div>
                  {/* Contract Value (revised) — right-aligned */}
                  <div className="px-3 text-xs leading-none text-foreground text-right flex-shrink-0" style={{ width: '140px' }}>
                    {fmtCAD(revisedContractValue)}
                  </div>
                  {/* Margin % */}
                  <div
                    className="px-3 text-xs leading-none text-right flex-shrink-0"
                    style={{ width: '80px', color: margin <= 0 ? '#DC2626' : '#16A34A' }}
                  >
                    {margin?.toFixed(2)}%
                  </div>
                  {/* Status */}
                  <div className="px-3 flex-shrink-0" style={{ width: '130px' }} onClick={(e) => e?.stopPropagation()}>
                    {canEditStatus(project) ? (
                      <ProjectStatusMenu
                        status={project?.status}
                        options={STATUS_DROPDOWN_OPTIONS}
                        saving={savingStatusIds?.includes(project?.id)}
                        label={(statusVal) => t(`projectStatus.${statusVal}`) || statusVal}
                        onChange={(statusVal) => handleStatusChange(project, statusVal)}
                      />
                    ) : (
                      <Badge variant={statusVariant('project', project?.status)}>
                        {t(`projectStatus.${project?.status}`) || project?.status}
                      </Badge>
                    )}
                  </div>
                  {/* Team avatars — priority: pm > coordinator > supervisor > assistant */}
                  <div
                    className="px-3 flex items-center flex-shrink-0"
                    style={{ width: '100px' }}
                    onClick={(e) => e?.stopPropagation()}
                  >
                    {project?.pm || project?.coordinator || project?.supervisor || project?.assistant ? (
                      <>
                        <TeamAvatar member={project?.pm} role={t('projectTeam.pm')} index={0} />
                        <TeamAvatar member={project?.coordinator} role={t('projectTeam.coordinator')} index={1} />
                        <TeamAvatar member={project?.supervisor} role={t('projectTeam.supervisor')} index={2} />
                        <TeamAvatar member={project?.assistant} role={t('projectTeam.assistant')} index={3} />
                      </>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </div>
                  {/* Actions — sticky right */}
                  <div
                    className="px-3 text-center flex-shrink-0 sticky right-0 bg-white relative"
                    style={{ width: '50px' }}
                    onClick={(e) => e?.stopPropagation()}
                  >
                    <button
                      ref={(el) => { actionBtnRefs.current[project?.id] = el; }}
                      onClick={(e) => toggleDropdown(e, project?.id)}
                      className="bg-transparent outline-none focus:outline-none focus:ring-2 focus:ring-primary/20 text-gray-500 hover:text-primary hover:bg-gray-50 rounded-full p-1 transition-colors leading-none"
                    >
                      ⋮
                    </button>
                    <PortalDropdown
                      anchorRef={{ current: actionBtnRefs?.current?.[project?.id] }}
                      open={openDropdownId === project?.id}
                      onClose={() => setOpenDropdownId(null)}
                      width={120}
                      items={[
                        {
                          label: t("projectList.filter.edit"),
                          onClick: (e) => handleEdit(e, project?.id),
                          className: "w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-700",
                        },
                        {
                          label: t("projectList.filter.archive"),
                          onClick: (e) => handleArchive(e, project?.id),
                          className: "w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-700",
                        },
                        {
                          label: t("projectList.filter.delete"),
                          onClick: (e) => handleDelete(e, project?.id),
                          className: "w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-700",
                        },
                      ]}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* New Project Modal */}
      <NewProjectModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProject(null);
        }}
        onCreate={editingProject ? handleUpdateProject : handleCreateProject}
        initialData={editingProject}
        isEditMode={!!editingProject}
      />
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setProjectToDelete(null);
        }}
        onConfirm={confirmDelete}
        projectName={projectToDelete?.name}
      />
    </div>
  );
};

export default ProjectDetailPage;
